import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Play,
  Images,
  ZoomIn,
  RefreshCw,
} from '@yingbaba/icons';
import type { Portfolio } from '@yingbaba/types';
import { Button, IconButton, Image, MediaPreviewDialog, Modal } from './index';
import {
  portfolioCover,
  portfolioImages,
  portfolioMediaURL,
  portfolioVideo,
  videoDuration,
  type PortfolioScope,
} from './portfolio';

export function PortfolioShowcase({
  item,
  scope = 'public',
}: {
  item: Portfolio;
  scope?: PortfolioScope;
}) {
  const video = portfolioVideo(item, scope);
  const images = portfolioImages(item);
  const [selected, setSelected] = useState(video?.kind === 'video' ? -1 : 0);
  const [started, setStarted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [preview, setPreview] = useState(false);
  const player = useRef<HTMLVideoElement>(null);
  const showVideo = selected === -1 && video?.kind === 'video';
  const photo = images[selected];
  const src = photo ? portfolioMediaURL(photo, scope) : portfolioCover(item, scope);
  const poster = portfolioCover(item, scope);
  const hasOwnCover = Boolean(
    item.media?.some((file) => file.kind === 'cover') ||
    images.length ||
    (item.cover && !item.cover.startsWith('/assets/')),
  );

  useEffect(() => {
    setSelected(video?.kind === 'video' ? -1 : 0);
    setStarted(false);
    setFailed(false);
    setPreview(false);
  }, [item.id, video?.src]);

  function select(index: number) {
    player.current?.pause();
    setSelected(index);
    setStarted(false);
    setFailed(false);
  }
  async function play(retry = false) {
    const element = player.current;
    if (!element) return;
    setFailed(false);
    if (retry) element.load();
    try {
      await element.play();
    } catch {
      if (player.current === element) setFailed(true);
    }
  }

  return (
    <div className="portfolio-showcase">
      <div className="portfolio-stage">
        {showVideo ? (
          <>
            <video
              ref={player}
              key={video.src}
              src={video.src}
              poster={hasOwnCover ? poster : undefined}
              controls
              playsInline
              preload="none"
              aria-label={`${item.title}作品视频`}
              onPlay={() => {
                setStarted(true);
                setFailed(false);
              }}
              onError={() => setFailed(true)}
            />
            {!started && !failed && (
              <button
                type="button"
                className="portfolio-play"
                aria-label="播放作品视频"
                onClick={() => void play()}
              >
                <Play size={28} fill="currentColor" />
              </button>
            )}
            {failed && (
              <div className="portfolio-video-error" role="alert">
                <strong>视频暂时无法播放</strong>
                <p>请重试，或联系制作团队确认作品地址与视频格式。</p>
                <Button variant="secondary" size="sm" onClick={() => void play(true)}>
                  <RefreshCw size={15} /> 重新加载
                </Button>
                {!item.media?.some((file) => file.kind === 'video') && (
                  <a href={video.src} target="_blank" rel="noopener noreferrer">
                    在新窗口查看 <ArrowUpRight size={14} />
                  </a>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className={`portfolio-photo ${hasOwnCover ? '' : 'reference-photo'}`}
              onClick={() => setPreview(true)}
              aria-label={`查看作品大图${images.length ? ` ${selected + 1}` : ''}`}
            >
              <Image
                src={src}
                fallbackSrc={hasOwnCover ? null : undefined}
                alt={photo?.name || item.title}
                eager
              />
              <span className="portfolio-zoom">
                <ZoomIn size={15} /> 查看大图
              </span>
            </button>
            {images.length > 1 && (
              <div className="portfolio-gallery-nav">
                <IconButton
                  label="上一张作品图片"
                  disabled={selected === 0}
                  onClick={() => select(selected - 1)}
                >
                  <ArrowLeft size={18} />
                </IconButton>
                <span aria-live="polite">
                  {selected + 1} / {images.length}
                </span>
                <IconButton
                  label="下一张作品图片"
                  disabled={selected === images.length - 1}
                  onClick={() => select(selected + 1)}
                >
                  <ArrowRight size={18} />
                </IconButton>
              </div>
            )}
          </>
        )}
      </div>
      <div className="portfolio-caption">
        <span>
          {showVideo
            ? `作品视频${video.duration ? ` · ${videoDuration(video.duration)}` : ''}`
            : images.length
              ? `作品图片 · ${selected + 1} / ${images.length}`
              : hasOwnCover
                ? '作品封面'
                : '参考配图 · 完整作品请向制作团队了解'}
        </span>
        {video?.kind === 'link' && (
          <a className="link" href={video.src} target="_blank" rel="noopener noreferrer">
            在原站查看视频 <ArrowUpRight size={15} />
          </a>
        )}
        {video?.kind === 'video' && selected !== -1 && (
          <button className="link" type="button" onClick={() => select(-1)}>
            <Play size={14} /> 返回作品视频
          </button>
        )}
      </div>
      {images.length > 0 && (
        <div className="portfolio-thumbnails" aria-label="作品图片选择">
          {video?.kind === 'video' && (
            <button
              type="button"
              className={showVideo ? 'active' : ''}
              onClick={() => select(-1)}
              aria-pressed={showVideo}
              aria-label="选择作品视频"
            >
              <Image src={poster} fallbackSrc={hasOwnCover ? null : undefined} alt="" />
              <span>
                <Play size={15} /> 视频
              </span>
            </button>
          )}
          {images.map((file, index) => (
            <button
              key={file.id}
              type="button"
              className={selected === index ? 'active' : ''}
              onClick={() => select(index)}
              aria-pressed={selected === index}
              aria-label={`选择作品图片 ${index + 1}`}
            >
              <Image src={portfolioMediaURL(file, scope)} fallbackSrc={null} alt="" />
              <span>
                <Images size={13} /> {index + 1}
              </span>
            </button>
          ))}
        </div>
      )}
      {!showVideo && (
        <MediaPreviewDialog
          open={preview}
          onOpenChange={setPreview}
          title={photo?.name || item.title}
          description={images.length ? `作品图片 ${selected + 1} / ${images.length}` : '作品封面'}
          kind="image"
          src={src}
        />
      )}
    </div>
  );
}

export function PortfolioPreview({
  item,
  scope = 'public',
}: {
  item: Portfolio;
  scope?: PortfolioScope;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        预览作品
      </Button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={item.title}
        description="作品视频、图片与创作说明"
      >
        <PortfolioShowcase item={item} scope={scope} />
        <p className="prose">{item.description}</p>
      </Modal>
    </>
  );
}
